import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpin } from '../../components/SpinContext';
import { useToken } from '../../common/helper';
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  message,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import {
  AddItemButton,
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomModal,
  CustomSelect,
  CustomShipmentText,
  CustomTitle,
} from '../../components';
import PolygonFenceMap from '../../components/PolygonFenceMap';
import usePolygonEditSession from '../../hooks/usePolygonEditSession';
import {
  CreateNewShipment,
  GetSimulatorAlertThresholds,
} from '../../apis/shipment';
import { AddSettingsLocation } from '../../apis/settings';
import {
  getCompanyCouriersDDL,
  getLocationDDL,
  getProductDDL,
  getTeamsDDLOFFeature,
} from '../../common/utils/dropDown';
import { useSelector } from 'react-redux';
import { getPermissions } from '../../common/helper/getPermissions';
import { MenuItemData } from '@/apis/types';
import SaveLocationModal from '../Modals/SaveLocationModal';
import { GetSettingsCompanySensors, SensorData } from '../../apis/settings';
import { sensorIgnoreTime } from '../../common/static_data';
import { GoogleMapsProvider } from '../../components/GoogleMapsProvider';
import EditLocation from '../../assets/images/edit_location.svg';
import LocationAutoCompleteShipment from '../Modals/LocationAutoCompleteShipment';
import ShipmentPreviewMap from './ShipmentPreviewMap';
import TeamDropdownSelect from '../../components/TeamDropdownSelect';
import CustomCheckboxGroup from '../../components/CustomCheckboxGroup';
import { DeleteIcon, GeoFenceIcon, UndoIcon } from '../../assets/icons/icons';
import emptyImg from '@/assets/images/emptyTransport.svg';
import { ErrorLike } from '@/apis/types';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

import EmptyTransportProvider from './partials/EmptyTransportProvider';
import { CourierData } from '@/apis/lookup';
import {
  countRowsByProviderDisplayName,
  formatTransportProviderOptionLabel,
  shouldShowProviderTeamLabel,
  teamsForTransportProviderLabel,
} from '@/common/utils/transportProviderOptionLabel';
import { inferCarrierFromTrackingId } from '@/common/utils/inferCarrierFromTrackingId';
import {
  SIM_COURIER_PROVIDER_KEY,
  SIM_SENSOR_PROVIDER_KEY,
  generateSimCourierId,
  isSimCourierId,
  isSimSensorId,
  simSensorId,
} from '@/common/utils/simulatorIds';
import { useSyntheticProvidersGate } from '@/hooks/useSyntheticProvidersGate';
import { GetProducts } from '@/apis/onboarding';
import SimulationScenarioView from './SimulationScenarioView';
import {
  AlertThresholds,
  ScenarioMetric,
  ScenarioProduct,
  SimulationScenarioConfig,
  productLimits,
  summarizeScenario,
  toScenarioProduct,
  toScenarioPayload,
  withSimulatorSensorRow,
} from './simulationScenario';
import { useSensorProductLinksGate } from '@/hooks/useSensorProductLinksGate';
import {
  buildSensorsPayloadFromRows,
  type SensorFormRow,
} from './sensorFormPayload';
import {
  fetchTiveAutocreateSensorId,
  hasCoordinates,
  hasEligibleTiveRow,
  runTiveAutocreate,
  TIVE_ADDRESS_REQUIRED_MESSAGE,
} from './tiveAutocreate';
import SettingsProductDrawerForm from '../Settings/SettingsProductDrawerForm';
import { usePaginatedProductDropdown } from '../../hooks/usePaginatedProductDropdown';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import {
  ProductMultiSelectField,
  ProductPicker,
  ProductQuantityRows,
  SensorSection,
} from './components';
import { applyProductQuantityDelta } from './shellShipmentHelpers';
import { nextSimSensorId } from './sensorProductLinks';
import { pickerCatalogProps } from './productPickerCatalog';
import { useSensorProductLinks } from './useSensorProductLinks';
import {
  MAX_QUANTITY_MESSAGE,
  MIN_QUANTITY_MESSAGE,
} from '@/utils/formatQuantity';
import './CreateShipmentDrawer.css';

const { useWatch } = Form;

type DrawerView = 'shipment' | 'product_form' | 'scenario';

type CourierSelectRow = CourierData & { courier_label: string };
type SensorSelectRow = SensorData & { sensor_label: string };

interface LatLng {
  lat: number | null;
  lng: number | null;
}

/** A drawn geofence vertex; always has both coordinates. */
interface PolygonPoint {
  lat: number;
  lng: number;
}

const DEFAULT_POLYGON_MAP_CENTER = { lat: 37.7749, lng: -122.4194 };

const hasValidLatLng = (point: LatLng | undefined): boolean =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng);

/** The point as non-null coordinates, or null when it is not a valid position. */
const toPolygonPoint = (point: LatLng | undefined): PolygonPoint | null =>
  hasValidLatLng(point) && point?.lat != null && point.lng != null
    ? { lat: point.lat, lng: point.lng }
    : null;

const SCENARIO_PRODUCT_MAX_PAGES = 10;
const SCENARIO_PRODUCT_RESULTS = 25;

interface SelectedProduct {
  id: string;
  quantity: number;
}

export interface Product {
  id: string;
  name: string;
}

interface LocationOption {
  id?: string;
  name?: string;
  placeName?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  polygon?: PolygonPoint[];
  zip_code?: string;
  country?: string;
}

export interface Team {
  id: string;
  name: string;
}

interface FormValues {
  courier_tracking_id?: string;
  courier?: string;
  sensor_tracking_id?: string;
  sensor?: string;
  sensor_rows?: SensorFormRow[];
  reference_number: string;
  link_code?: string;
  teams?: Team[];
  products?: string[];
  ignore_sensor_measurement_at_start?: string;
  from_location?: string;
  to_location?: string;
  [key: string]: unknown;
}

interface CreateShipmentDrawerProps {
  handleCloseDrawer: () => void;
  onSuccess: (data: unknown) => void;
}

interface RootState {
  teams: {
    list: Team[];
  };
  menu: {
    items: MenuItemData[];
  };
}

/**
 * Drawer component for creating a new shipment with tracking details
 *
 * @param handleCloseDrawer - Callback to close the drawer
 * @param onSuccess - Callback when shipment is created successfully
 */
const CreateShipmentDrawer: React.FC<CreateShipmentDrawerProps> = ({
  handleCloseDrawer,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { xs, sm } = useBreakpoints();
  const isMobile = xs || sm;
  const [locationList, setLocationList] = useState<LocationOption[]>([]);
  const [fromLocation, setFromLocation] = useState<LocationOption | null>(null);
  const [toLocation, setToLocation] = useState<LocationOption | null>(null);
  const [showSensorFields, setShowSensorFields] = useState<boolean>(false);
  const linksEnabled = useSensorProductLinksGate();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [couriersList, setCouriersList] = useState<CourierSelectRow[]>([]);
  const [sensorsList, setSensorsList] = useState<SensorSelectRow[]>([]);
  const [tiveAutocreateSensorId, setTiveAutocreateSensorId] = useState('');
  const [createInTive, setCreateInTive] = useState(false);
  const { token: storedToken, removeToken } = useToken();
  // API helpers take a string; an expired session is handled by the interceptor.
  const token = storedToken ?? '';
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm();
  const [productDrawerForm] = Form.useForm();
  const [drawerView, setDrawerView] = useState<DrawerView>('shipment');
  // ZoomLogi Simulator scenario chosen in the scenario view; null = default journey.
  const [simulationScenario, setSimulationScenario] =
    useState<SimulationScenarioConfig | null>(null);
  // The company's alert settings, which size a scenario to fire its alert.
  const [alertThresholds, setAlertThresholds] =
    useState<AlertThresholds | null>(null);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);

  const handleUnauthorized = useCallback(
    (error: ErrorLike | unknown): void => {
      const err = error as ErrorLike;
      if (err && err?.status === 401) {
        removeToken();
        const _ = navigate('/login');
      }
    },
    [removeToken, navigate]
  );

  const productDropdown = usePaginatedProductDropdown({
    token,
    productsRefreshKey,
    unauthorize: handleUnauthorized,
  });
  const {
    products: paginatedProducts,
    getProductName,
    mergeIntoLookup,
    setSearch: setProductSearch,
    searchInputKey: productSearchInputKey,
    loading: productDropdownLoading,
    catalogTotalCount: productCatalogTotalCount,
    search: productSearch,
    handlePopupScroll: handleProductPopupScroll,
  } = productDropdown;

  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );

  const {
    sensorRows,
    sensorCardOpen,
    setSensorCardOpen,
    changeSensorRows,
    changeShipmentProducts,
    loadAllProductIds,
    openAddProduct,
    finishAddProduct,
  } = useSensorProductLinks({
    selectedProducts,
    setSelectedProducts,
    getProductName,
    mergeIntoLookup,
    loadCatalog: () => getProductDDL(token),
    openProductForm: () => setDrawerView('product_form'),
  });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalName, setModalName] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [polygonFrom, setPolygonFrom] = useState<PolygonPoint[]>([]);
  const [polygonTo, setPolygonTo] = useState<PolygonPoint[]>([]);

  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);

  const [selectedLatLng1, setSelectedLatLng1] = useState<LatLng>({
    lat: null,
    lng: null,
  });
  const [selectedLatLng2, setSelectedLatLng2] = useState<LatLng>({
    lat: null,
    lng: null,
  });
  const polygonSession = usePolygonEditSession();

  const [fromDisplayValue, setFromDisplayValue] = useState<string>('');
  const [toDisplayValue, setToDisplayValue] = useState<string>('');
  const [fromEditMode, setFromEditMode] = useState(false);
  const [toEditMode, setToEditMode] = useState(false);
  // IDs of locations created inline this session — used to assign teams at shipment-save time
  const [newFromLocId, setNewFromLocId] = useState<string | null>(null);
  const [newToLocId, setNewToLocId] = useState<string | null>(null);
  const [saveLocationModalOpen, setSaveLocationModalOpen] = useState(false);
  const [saveLocationTarget, setSaveLocationTarget] = useState<
    'from' | 'to' | null
  >(null);

  // keep a ref so bookmark handlers always see the latest fromLocation/toLocation
  const fromLocationRef = useRef<LocationOption | null>(null);
  const toLocationRef = useRef<LocationOption | null>(null);
  const fromLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  const toLatLngRef = useRef<{ lat: number | null; lng: number | null }>({
    lat: null,
    lng: null,
  });
  // True after the user confirms a polygon edit (draw or clear) for that field;
  // blocks late GetSettingsLocationById enrichment from restoring the old fence.
  const polygonFromEditedRef = useRef(false);
  const polygonToEditedRef = useRef(false);
  // Explicit detail-hydration gate for geo-fence (not inferred from nullable lat).
  const [fromDetailHydrating, setFromDetailHydrating] = useState(false);
  const [toDetailHydrating, setToDetailHydrating] = useState(false);
  useEffect(() => {
    fromLocationRef.current = fromLocation;
  }, [fromLocation]);
  useEffect(() => {
    toLocationRef.current = toLocation;
  }, [toLocation]);

  // teams for drop down
  const teamsList = useSelector((state: RootState) => state.teams.list);
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const LocationsPermissions = getPermissions(
    main_menu,
    'Settings',
    'Locations'
  );
  const canCreateLocation = LocationsPermissions?.is_create ?? false;

  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamsSearch, setTeamsSearch] = useState('');
  const [debouncedTeamsSearch, setDebouncedTeamsSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedTeamsSearch(teamsSearch);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [teamsSearch]);

  useEffect(() => {
    let cancelled = false;
    const teamsRequest = debouncedTeamsSearch
      ? getTeamsDDLOFFeature(token, 'Shipment', debouncedTeamsSearch)
      : getTeamsDDLOFFeature(token, 'Shipment');

    teamsRequest
      .then((val) => {
        if (cancelled) return;
        const teams = val || [];
        setAllTeams(teams);
      })
      .catch(handleUnauthorized);
    return () => {
      cancelled = true;
    };
  }, [debouncedTeamsSearch, token, handleUnauthorized]);

  const handleTeamsSearch = useCallback((val: string) => {
    setTeamsSearch(val);
  }, []);

  const handleDetailHydrationChange = useCallback(
    (fieldName: string, phase: 'start' | 'done') => {
      const pending = phase === 'start';
      if (fieldName === 'from_location') setFromDetailHydrating(pending);
      else if (fieldName === 'to_location') setToDetailHydrating(pending);
    },
    []
  );

  const openMapModal = (name: string): void => {
    // Gate on explicit detail-request state — not nullable lat/lng (a saved
    // location may legitimately have null coords, and failed fetches settle).
    if (
      (name === 'from_location' && fromDetailHydrating) ||
      (name === 'to_location' && toDetailHydrating)
    ) {
      return;
    }
    setLocationName(name);
    setModalName('Draw Polygon');
    const committed = name === 'from_location' ? polygonFrom : polygonTo;
    polygonSession.load(committed, committed.length >= 3);
    setModalOpen(true);
  };

  const onModalConfirm = (): void => {
    if (polygonSession.isValid || polygonSession.isEmpty) {
      const nextPolygon = polygonSession.polygon;
      if (locationName === 'from_location') {
        setPolygonFrom(nextPolygon);
        polygonFromEditedRef.current = true;
        // Keep fromLocation.polygon in sync so onFinish fallback cannot restore
        // a cleared fence from the hydrated location record.
        setFromLocation((prev) => {
          if (!prev) return prev;
          const next = { ...prev, polygon: nextPolygon };
          fromLocationRef.current = next;
          return next;
        });
      } else {
        setPolygonTo(nextPolygon);
        polygonToEditedRef.current = true;
        setToLocation((prev) => {
          if (!prev) return prev;
          const next = { ...prev, polygon: nextPolygon };
          toLocationRef.current = next;
          return next;
        });
      }
      message.success(
        polygonSession.isEmpty ? 'Polygon removed.' : 'Polygon created'
      );
      setModalOpen(false);
    } else {
      message.error('Please close the polygon before saving.');
    }
  };

  const handleSetModalOpen = (item: boolean): void => {
    if (item === false) {
      polygonSession.discard();
      setModalOpen(false);
    }
  };

  const polygonMapCenter: PolygonPoint =
    toPolygonPoint(
      locationName === 'from_location' ? selectedLatLng1 : selectedLatLng2
    ) ?? DEFAULT_POLYGON_MAP_CENTER;

  const handleBookmarkClick = (target: 'from' | 'to'): void => {
    const addressStr = target === 'from' ? fromDisplayValue : toDisplayValue;
    if (!addressStr) return;

    setSaveLocationTarget(target);
    setSaveLocationModalOpen(true);
  };

  const handleSaveLocation = async (locationName: string): Promise<void> => {
    const target = saveLocationTarget;
    if (!target) return;
    const fieldName = target === 'from' ? 'from_location' : 'to_location';
    const displayValue = target === 'from' ? fromDisplayValue : toDisplayValue;
    const locRef =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    const addressStr = locRef?.address || displayValue;
    const latLng =
      target === 'from' ? fromLatLngRef.current : toLatLngRef.current;

    try {
      const res = await AddSettingsLocation(token, {
        name: locationName,
        address: addressStr,
        latitude: latLng.lat,
        longitude: latLng.lng,
        zip_code: locRef?.zip_code ?? '',
        country: locRef?.country ?? '',
        teams: selectedTeams.map((t) => t.id),
        polygon: target === 'from' ? polygonFrom : polygonTo,
        contacts: [],
        receiving_hours: { start: null, end: null },
        location_type: null,
        id: null,
      });

      const resData = (
        res as {
          data?: {
            data?: {
              is_success?: boolean;
              data?: {
                created_locations?: LocationOption & { address?: string };
              };
            };
          };
        }
      )?.data?.data;
      if (!resData?.is_success) {
        throw new Error('API returned is_success: false');
      }

      const created = resData?.data?.created_locations;
      const newLoc: LocationOption & { address?: string } = {
        ...(created ?? {}),
        id: created?.id,
        name: created?.name ?? locationName,
        address: created?.address ?? addressStr,
        latitude: latLng.lat ?? undefined,
        longitude: latLng.lng ?? undefined,
      };

      // Track this location's ID so we can assign teams when the shipment is saved
      if (target === 'from') setNewFromLocId(created?.id ?? null);
      else setNewToLocId(created?.id ?? null);

      // Optimistically prepend so the new entry is available immediately,
      // even if the background refresh fails.
      setLocationList((prev) => [newLoc, ...prev]);
      // dropdown=true: id/name/address only — same as Create shell; full list can
      // 504/time out and leave autocomplete with Google-only suggestions.
      getLocationDDL(token, true)
        .then((refreshed) => {
          if (refreshed?.length > 0) setLocationList(refreshed);
        })
        .catch(() => {
          /* silent — optimistic entry is already in list */
        });

      handleAddressPlaceSelect(newLoc, fieldName);
      setSaveLocationModalOpen(false);
      message.success('Location saved');
    } catch (error) {
      message.error('Failed to save location. Please try again.');
      throw error;
    }
  };

  const handleDetachLocation = (target: 'from' | 'to'): void => {
    const locRef =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    // Use the stored address directly to avoid string-splitting bugs when the
    // location name itself contains ' · '.
    const addressOnly =
      (locRef as LocationOption & { address?: string })?.address || '';
    const fieldName = target === 'from' ? 'from_location' : 'to_location';
    // Preserve coordinates from the detached location so the user can
    // immediately save it as a new location without re-selecting from dropdown.
    const detachLat = locRef?.latitude != null ? Number(locRef.latitude) : null;
    const detachLng =
      locRef?.longitude != null ? Number(locRef.longitude) : null;
    if (target === 'from') {
      // Synchronously null the ref before state updates so any onChange
      // callbacks Ant Design fires during AutoComplete remount do not read
      // stale location metadata. Note: if onChange fires with no location id,
      // handleAddressInputChange will still clear selectedLatLng1.
      fromLocationRef.current = null;
      setFromLocation(null);
      setFromDisplayValue(addressOnly);
      setNewFromLocId(null);
      setSelectedLatLng1({ lat: detachLat, lng: detachLng });
      setFromDetailHydrating(false);
    } else {
      toLocationRef.current = null;
      setToLocation(null);
      setToDisplayValue(addressOnly);
      setNewToLocId(null);
      setSelectedLatLng2({ lat: detachLat, lng: detachLng });
      setToDetailHydrating(false);
    }
    form.setFieldsValue({ [fieldName]: addressOnly });
  };

  const handleAddressInputChange = (
    target: 'from' | 'to',
    value: string
  ): void => {
    const loc =
      target === 'from' ? fromLocationRef.current : toLocationRef.current;
    if (!loc?.id) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        setSelectedLatLng1({ lat: null, lng: null });
        setFromDisplayValue(value);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        setSelectedLatLng2({ lat: null, lng: null });
        setToDisplayValue(value);
      }
      return;
    }
    const locWithAddress = loc as LocationOption & { address?: string };
    const expected =
      loc.id && locWithAddress.address && !loc.placeName
        ? `${loc.name} · ${locWithAddress.address}`
        : loc.placeName || loc.name || '';
    if (value !== expected) {
      if (target === 'from') {
        fromLatLngRef.current = { lat: null, lng: null };
        setFromLocation(null);
        setFromDisplayValue(value);
        setSelectedLatLng1({ lat: null, lng: null });
        setFromEditMode(false);
        setNewFromLocId(null);
        setFromDetailHydrating(false);
      } else {
        toLatLngRef.current = { lat: null, lng: null };
        setToLocation(null);
        setToDisplayValue(value);
        setSelectedLatLng2({ lat: null, lng: null });
        setToEditMode(false);
        setNewToLocId(null);
        setToDetailHydrating(false);
      }
    }
  };

  // Form submission handler

  const handleAddressPlaceSelect = (
    place: LocationOption & { address?: string },
    fieldName: string,
    options?: { isDetailEnrichment?: boolean }
  ): void => {
    const lat = place?.latitude != null ? Number(place.latitude) : null;
    const lng = place?.longitude != null ? Number(place.longitude) : null;
    const displayName =
      place.id && place.address && !place.placeName
        ? `${place.name} · ${place.address}`
        : place.placeName || place.name || '';
    if (fieldName === 'from_location') {
      const prev = fromLocationRef.current;
      // Only GetSettingsLocationById enrichment takes the soft-merge path —
      // never a fresh lightweight select of the same id with null/missing lat.
      const sameIdEnrichment =
        !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
      fromLatLngRef.current = { lat, lng };
      setSelectedLatLng1({ lat: lat, lng: lng });
      setFromDisplayValue(displayName);
      // Soft-merge detail hydration: keep a post-select polygon edit (draw or
      // clear) and do not snap edit-mode closed when enrichment lands after the
      // user clicked the saved chip. Read polygon from the location ref
      // (updated synchronously in onModalConfirm) — not polygonFrom state.
      if (sameIdEnrichment) {
        const enriched = polygonFromEditedRef.current
          ? { ...place, polygon: prev?.polygon ?? [] }
          : place;
        fromLocationRef.current = enriched;
        setFromLocation(enriched);
        if (!polygonFromEditedRef.current) {
          setPolygonFrom(place.polygon ?? []);
        }
      } else {
        polygonFromEditedRef.current = false;
        fromLocationRef.current = place;
        setFromLocation(place);
        setPolygonFrom(place.polygon ?? []);
        setFromEditMode(false);
      }
    } else if (fieldName === 'to_location') {
      const prev = toLocationRef.current;
      const sameIdEnrichment =
        !!options?.isDetailEnrichment && !!prev?.id && prev.id === place.id;
      toLatLngRef.current = { lat, lng };
      setSelectedLatLng2({ lat: lat, lng: lng });
      setToDisplayValue(displayName);
      if (sameIdEnrichment) {
        const enriched = polygonToEditedRef.current
          ? { ...place, polygon: prev?.polygon ?? [] }
          : place;
        toLocationRef.current = enriched;
        setToLocation(enriched);
        if (!polygonToEditedRef.current) {
          setPolygonTo(place.polygon ?? []);
        }
      } else {
        polygonToEditedRef.current = false;
        toLocationRef.current = place;
        setToLocation(place);
        setPolygonTo(place.polygon ?? []);
        setToEditMode(false);
      }
    }
    form.setFieldsValue({ [fieldName]: displayName });
  };

  //Making dropdwon Calls for transport providers

  useEffect(() => {
    const _ = getCompanyCouriersDDL(token).then((val) => {
      if (val && val.length > 0) {
        const rows = val as CourierData[];
        const nameCounts = countRowsByProviderDisplayName(
          rows,
          (r) => r.courier_name
        );
        setCouriersList(
          rows.map((row) => {
            const showTeams = shouldShowProviderTeamLabel(
              row.courier_name,
              nameCounts
            );
            return {
              ...row,
              courier_label: formatTransportProviderOptionLabel(
                row.courier_name,
                teamsForTransportProviderLabel(row),
                { showTeamDisambiguation: showTeams }
              ),
            };
          })
        );
      }
    });
  }, [token]);

  useEffect(() => {
    GetSettingsCompanySensors(token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            if (data.data.data && data.data.data.length > 0) {
              const sensorRows = data.data.data as SensorData[];
              const nameCounts = countRowsByProviderDisplayName(
                sensorRows,
                (r) => r.sensor_name
              );
              setSensorsList(
                sensorRows.map((row) => {
                  const showTeams = shouldShowProviderTeamLabel(
                    row.sensor_name,
                    nameCounts
                  );
                  return {
                    ...row,
                    sensor_label: formatTransportProviderOptionLabel(
                      row.sensor_name,
                      teamsForTransportProviderLabel(row),
                      { showTeamDisambiguation: showTeams }
                    ),
                  };
                })
              );
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch(handleUnauthorized);
  }, [token, handleUnauthorized]);

  useEffect(() => {
    const _ = getLocationDDL(token, true).then((val) => {
      if (val && val.length > 0) {
        setLocationList(val);
      }
    });
  }, [token]);

  const onFinish = (values: FormValues): void => {
    if (linksEnabled && sensorCardOpen) {
      message.error("Save or cancel the sensor you're editing");
      return;
    }
    if (values && Object.keys(values).length > 0) {
      const model: Record<string, unknown> = { ...values };
      model.id = null;
      const teamIDs = values.teams?.map((item: Team) => item?.id);
      model.teams = teamIDs;
      if (fromLocation) {
        if (fromLocation.id) {
          // Existing ids: send polygon only when the user edited it (draw or
          // clear). Re-sending a hydrated fence is redundant (already on
          // LocationV2) and unsafe when O/D share the same id — an unedited
          // destination payload can overwrite an origin clear on flush.
          const { polygon: _fromPoly, ...fromRest } = fromLocation;
          const fromPayload: Record<string, unknown> = { ...fromRest };
          if (polygonFromEditedRef.current) {
            fromPayload.polygon = polygonFrom;
          }
          model.from_location = fromPayload;
        } else {
          model.from_address =
            (fromLocation as LocationOption & { address?: string }).address ||
            fromLocation.placeName;
          delete model.from_location;
        }
      }

      if (toLocation) {
        if (toLocation.id) {
          const { polygon: _toPoly, ...toRest } = toLocation;
          const toPayload: Record<string, unknown> = { ...toRest };
          if (polygonToEditedRef.current) {
            toPayload.polygon = polygonTo;
          }
          model.to_location = toPayload;
        } else {
          model.to_address =
            (toLocation as LocationOption & { address?: string }).address ||
            toLocation.placeName;
          delete model.to_location;
        }
      }

      const built = buildSensorsPayloadFromRows(
        linksEnabled ? sensorRows : values.sensor_rows,
        sensorsList,
        { includeProductIds: linksEnabled }
      );
      if (built.ok === false) {
        message.error(built.message);
        return;
      }
      if (built.sensors.length > 20) {
        message.warning(
          'You added more than 20 sensors. Please confirm all IDs are correct before saving.'
        );
      }

      delete model.sensor;
      delete model.sensor_tracking_id;
      delete model.sensor_rows;

      if (built.sensors.length > 0) {
        model.sensors = built.sensors;
      }

      if (!model.courier && built.sensors.length === 0) {
        message.error(
          `At least one of sensor or ${theme.themeType === 2 ? 'carrier' : 'transport provider'} must be filled`
        );
        return;
      }

      if (
        createInTive &&
        !(hasCoordinates(fromLocation) && hasCoordinates(toLocation))
      ) {
        message.error(TIVE_ADDRESS_REQUIRED_MESSAGE);
        return;
      }

      if (ignoreSensorAlerts?.length === 0) {
        model.ignore_sensor_measurement_at_start = null;
      }

      const productPayload = selectedProducts
        .filter((p) => p.id && p.id !== 'all_products' && p.quantity)
        .map((p) => ({
          product_id: p.id,
          quantity: p.quantity,
        }));

      model.products = productPayload;

      const companyCourierId = values.courier;
      if (companyCourierId) {
        const match = couriersList.find(
          (c) => String(c.id) === String(companyCourierId)
        );
        const courierFk = match?.courier ?? match?.courier_id;
        if (courierFk) {
          model.courier = courierFk;
        }
      }

      if (simulationScenario) {
        model.simulation_scenario = toScenarioPayload(simulationScenario);
        model.temperature_min = simulationScenario.temperature_min;
        model.temperature_max = simulationScenario.temperature_max;
      }

      submitShipment(model);
    }
  };
  const sensorRowsWatch = useWatch('sensor_rows', form) as
    SensorFormRow[] | undefined;
  const activeSensorRows = linksEnabled ? sensorRows : sensorRowsWatch;
  const hasAnySensorForIgnoreUi = Boolean(
    activeSensorRows?.some(
      (r) =>
        (r?.sensor && String(r.sensor).length > 0) ||
        (r?.sensor_tracking_id &&
          String(r.sensor_tracking_id).trim().length > 0)
    )
  );
  const canCreateInTive = hasEligibleTiveRow(
    activeSensorRows,
    sensorsList,
    tiveAutocreateSensorId
  );
  const courierTrackingIdWatched = useWatch('courier_tracking_id', form);
  const courierWatched = useWatch('courier', form);
  const simulatorEnabled = useSyntheticProvidersGate();

  // ZoomLogi Simulator: ids are SIM-... and fill themselves on selection.
  const isSimulatorCourier = useCallback(
    (value: unknown): boolean =>
      couriersList.some(
        (c) =>
          (c.id ?? c.courier_id) === value &&
          c.provider_key === SIM_COURIER_PROVIDER_KEY
      ),
    [couriersList]
  );
  const isSimulatorSensor = useCallback(
    (value: unknown): boolean =>
      sensorsList.some(
        (s) =>
          (s.id ?? s.sensor_id) === value &&
          s.provider_key === SIM_SENSOR_PROVIDER_KEY
      ),
    [sensorsList]
  );
  const simulatorCourierSelected =
    simulatorEnabled && isSimulatorCourier(courierWatched);
  const simulatorInUse =
    simulatorEnabled &&
    (isSimulatorCourier(courierWatched) ||
      Boolean(activeSensorRows?.some((row) => isSimulatorSensor(row?.sensor))));

  // A scenario only makes sense while a simulator courier or sensor is selected.
  useEffect(() => {
    if (!simulatorInUse) {
      setSimulationScenario(null);
    }
  }, [simulatorInUse]);

  useEffect(() => {
    if (
      simulatorCourierSelected &&
      !form.getFieldValue('courier_tracking_id')
    ) {
      form.setFieldsValue({ courier_tracking_id: generateSimCourierId() });
    }
  }, [simulatorCourierSelected, form]);

  useEffect(() => {
    if (!simulatorEnabled || !sensorRowsWatch?.length) {
      return;
    }
    const courierId = form.getFieldValue('courier_tracking_id');
    let base: string | null = isSimCourierId(courierId)
      ? courierId.trim()
      : null;
    let simIndex = 0;
    let changed = false;
    const nextRows = sensorRowsWatch.map((row) => {
      if (!row || !isSimulatorSensor(row.sensor)) {
        return row;
      }
      simIndex += 1;
      if (row.sensor_tracking_id) {
        return row;
      }
      base = base ?? generateSimCourierId();
      changed = true;
      return { ...row, sensor_tracking_id: simSensorId(base, simIndex) };
    });
    if (changed) {
      form.setFieldsValue({ sensor_rows: nextRows });
    }
  }, [sensorRowsWatch, simulatorEnabled, isSimulatorSensor, form]);

  useEffect(() => {
    const _ = fetchTiveAutocreateSensorId(token).then(
      setTiveAutocreateSensorId
    );
  }, [token]);

  useEffect(() => {
    if (!canCreateInTive) {
      setCreateInTive(false);
    }
  }, [canCreateInTive]);

  useEffect(() => {
    const tid =
      typeof courierTrackingIdWatched === 'string'
        ? courierTrackingIdWatched
        : '';
    if (!tid.trim()) {
      form.setFieldsValue({ courier: undefined });
      return;
    }
    const inferred = inferCarrierFromTrackingId(tid);
    if (!inferred) {
      return;
    }
    if (couriersList.length === 0) {
      return;
    }
    // Match Settings-style titles (e.g. "UPS"). Extend if names vary (e.g. "DHL Express").
    const matches = couriersList.filter(
      (c) =>
        (c.courier_name ?? '').trim().toLowerCase() === inferred.toLowerCase()
    );
    if (matches.length !== 1) {
      return;
    }
    form.setFieldsValue({ courier: matches[0].id });
  }, [courierTrackingIdWatched, couriersList, form]);

  useEffect(() => {
    if (!showSensorFields) {
      form.setFieldsValue({
        sensor_tracking_id: undefined,
        sensor: undefined,
        sensor_rows: undefined,
      });
    }
  }, [form, showSensorFields]);

  const onFormValueCahnges = (): void => {
    const rows = (form.getFieldValue('sensor_rows') || []) as SensorFormRow[];
    if (rows.length > 0) {
      const trackingIdFieldPaths = rows.map((_, index) => [
        'sensor_rows',
        index,
        'sensor_tracking_id',
      ]);
      form.validateFields(trackingIdFieldPaths).catch(() => {});
    }
    if (
      !form.getFieldValue('courier_tracking_id') &&
      !form.getFieldValue('courier')
    ) {
      form.setFields([{ name: 'courier_tracking_id', errors: [] }]);
      form.setFields([{ name: 'courier', errors: [] }]);
    }
    // from location
    if (!form.getFieldValue('from_location')) {
      setFromLocation(null);
    }
    // to location
    if (!form.getFieldValue('to_location')) {
      setToLocation(null);
    }
  };

  const unauthorize = handleUnauthorized;

  const submitShipment = (model: Record<string, unknown>): void => {
    setIsFormValid(true);
    showSpin();
    CreateNewShipment(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            // Assign teams to any locations created inline during this session.
            // Done fire-and-forget after shipment succeeds so it never blocks the user.
            const teamIds = selectedTeams.map((t) => t.id);
            if (teamIds.length > 0) {
              const locUpdates: Promise<unknown>[] = [];
              if (
                newFromLocId &&
                fromLocationRef.current?.id === newFromLocId
              ) {
                locUpdates.push(
                  AddSettingsLocation(token, {
                    id: newFromLocId,
                    teams: teamIds,
                  }).catch(() => {})
                );
              }
              if (newToLocId && toLocationRef.current?.id === newToLocId) {
                locUpdates.push(
                  AddSettingsLocation(token, {
                    id: newToLocId,
                    teams: teamIds,
                  }).catch(() => {})
                );
              }
              // No await — fire and forget
              Promise.all(locUpdates).catch(() => {});
            }
            message.success(data?.data?.message);
            const createdShipmentId = (
              data?.data?.data as { id?: string } | undefined
            )?.id;
            if (createInTive) {
              if (createdShipmentId) {
                const _ = runTiveAutocreate(token, String(createdShipmentId));
              } else {
                message.warning(
                  'Shipment saved, but it could not be created in Tive. You can retry from the shipment.'
                );
              }
            }
            handleCloseDrawer();
            onSuccess(data?.data?.data);
            //  onShipmentSuccess();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error: ErrorLike) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  const [ignoreSensorAlerts, setIgnoreSensorAlerts] = useState<string[]>([]);

  // product quantity

  const updateQuantity = (id: string, delta: number): void => {
    const { next, error } = applyProductQuantityDelta(
      selectedProducts,
      id,
      delta
    );
    if (error === 'min') {
      message.warning(MIN_QUANTITY_MESSAGE);
      return;
    }
    if (error === 'max') {
      message.warning(MAX_QUANTITY_MESSAGE);
      return;
    }
    setSelectedProducts(next);
  };

  const removeProduct = (id: string | number): void => {
    let updated = selectedProducts.filter((p) => p.id !== id);
    updated = updated.filter((p) => p.id !== 'all_products');
    setSelectedProducts(updated);
    form.setFieldsValue({
      products: updated.map((p) => p.id),
    });
  };

  const renderProductPicker = (
    value: string[],
    onChange: (ids: string[]) => void,
    ariaLabel: string
  ): React.ReactNode => (
    <ProductPicker
      ariaLabel={ariaLabel}
      selectedIds={value}
      onChange={onChange}
      {...pickerCatalogProps(productDropdown)}
      getProductName={getProductName}
      loadAllProductIds={loadAllProductIds}
      onOpenAddProduct={() =>
        openAddProduct((productId) => onChange([...value, productId]))
      }
    />
  );

  const suggestSensorId = (
    provider: string,
    rows: SensorFormRow[]
  ): string | undefined => {
    if (!simulatorEnabled || !isSimulatorSensor(provider)) return undefined;
    const courierId = form.getFieldValue('courier_tracking_id');
    const base = isSimCourierId(courierId)
      ? courierId.trim()
      : generateSimCourierId();
    return nextSimSensorId(rows, base, isSimulatorSensor);
  };

  // The product list has no filter for limits, so page through until enough
  // products with limits for the scenario's metric turn up.
  const loadScenarioProducts = async (
    search: string,
    metric: ScenarioMetric
  ): Promise<ScenarioProduct[]> => {
    const matches: ScenarioProduct[] = [];
    let pageCount = 1;
    for (
      let page = 1;
      page <= Math.min(pageCount, SCENARIO_PRODUCT_MAX_PAGES) &&
      matches.length < SCENARIO_PRODUCT_RESULTS;
      page += 1
    ) {
      const response = await GetProducts(token, page, search || undefined);
      const payload = response?.data?.data;
      if (!payload?.is_success) break;
      pageCount = payload.page_count ?? 1;
      for (const product of payload.data ?? []) {
        const scenarioProduct = toScenarioProduct(product);
        if (productLimits(scenarioProduct, metric)) {
          matches.push(scenarioProduct);
        }
      }
    }
    return matches;
  };

  // Read the alert settings of the shipment's teams each time the builder opens;
  // without them (an error, or the gate off) the builder keeps its defaults.
  const scenarioTeamKey = selectedTeams.map((t) => t.id).join(',');
  useEffect(() => {
    if (drawerView !== 'scenario') return undefined;
    let cancelled = false;
    GetSimulatorAlertThresholds(
      token,
      scenarioTeamKey ? scenarioTeamKey.split(',') : []
    )
      .then((response) => {
        if (!cancelled) setAlertThresholds(response?.data?.templates ?? null);
      })
      .catch(() => {
        if (!cancelled) setAlertThresholds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [drawerView, scenarioTeamKey, token]);

  // A scenario's readings come from the simulator sensor and its product sets
  // the limits, so saving one adds both to the shipment (the product also goes
  // on the sensor when sensors carry products).
  const applyScenarioChoices = (
    config: SimulationScenarioConfig,
    previousProductId: string | undefined
  ): void => {
    const { product } = config;
    const productChanged = previousProductId !== product?.id;
    const simulatorSensor = sensorsList.find(
      (s) => s.provider_key === SIM_SENSOR_PROVIDER_KEY
    );
    const simulatorSensorId = simulatorSensor?.id ?? simulatorSensor?.sensor_id;
    const keepsPrevious =
      linksEnabled &&
      sensorRows.some(
        (row) =>
          !isSimulatorSensor(row.sensor) &&
          previousProductId !== undefined &&
          row.product_ids?.includes(previousProductId)
      );

    if (product) {
      mergeIntoLookup([{ id: product.id, name: product.name }]);
    }
    setSelectedProducts((prev) => {
      let next = prev;
      if (productChanged && previousProductId && !keepsPrevious) {
        next = next.filter((p) => p.id !== previousProductId);
      }
      if (product && !next.some((p) => p.id === product.id)) {
        next = [...next, { id: product.id, quantity: 1 }];
      }
      return next;
    });

    if (!simulatorSensorId) return;
    if (linksEnabled) {
      // Saved sensors live in state, not the form; keep complete rows.
      const sensorId = String(simulatorSensorId);
      const productIds = (existing: string[] | undefined): string[] => {
        const kept = (existing ?? []).filter(
          (id) => id !== previousProductId && id !== product?.id
        );
        return product ? [...kept, product.id] : kept;
      };
      const hasRow = sensorRows.some((row) => isSimulatorSensor(row.sensor));
      changeSensorRows(
        hasRow
          ? sensorRows.map((row) =>
              isSimulatorSensor(row.sensor)
                ? { ...row, product_ids: productIds(row.product_ids) }
                : row
            )
          : [
              ...sensorRows,
              {
                sensor: sensorId,
                sensor_tracking_id: suggestSensorId(sensorId, sensorRows),
                product_ids: productIds(undefined),
              },
            ]
      );
      return;
    }
    setShowSensorFields(true);
    form.setFieldsValue({
      sensor_rows: withSimulatorSensorRow(
        form.getFieldValue('sensor_rows') as SensorFormRow[] | undefined,
        String(simulatorSensorId),
        isSimulatorSensor
      ),
    });
  };

  const sensorOptions = (
    <>
      {canCreateInTive && (
        <Col span={24}>
          <div className='create-shipment-checkbox-row'>
            <CustomCheckboxGroup
              style={{ fontWeight: '600', fontSize: '12px' }}
              options={[
                {
                  label: (
                    <span className='create-shipment-label-text'>
                      Create shipment in Tive
                    </span>
                  ),
                  value: 'Create shipment in Tive',
                },
              ]}
              selectedValues={createInTive ? ['Create shipment in Tive'] : []}
              onChange={(values) => {
                setCreateInTive((values as string[]).length > 0);
              }}
            />
          </div>
        </Col>
      )}

      {/* Ignore sensor measurement at start — directly under sensor controls */}
      {hasAnySensorForIgnoreUi && (
        <Col span={24}>
          <Flex align='center' gap={8} wrap='wrap' style={{ padding: '8px 0' }}>
            <CustomCheckboxGroup
              style={{
                fontWeight: '600',
                fontSize: '12px',
                flex: 'none',
              }}
              options={[
                {
                  label: (
                    <span className='create-shipment-label-text'>
                      {ignoreSensorAlerts?.length > 0
                        ? 'Ignore readings pre-pickup and for'
                        : 'Ignore readings pre-pickup'}
                    </span>
                  ),
                  value: 'Ignore sensor measurement at start',
                },
              ]}
              selectedValues={ignoreSensorAlerts}
              onChange={(values) => {
                setIgnoreSensorAlerts(values as string[]);
              }}
            />
            {ignoreSensorAlerts?.length > 0 && (
              <>
                <Form.Item
                  className='custom-input form-input-style'
                  name='ignore_sensor_measurement_at_start'
                  noStyle
                >
                  <CustomSelect
                    placeholder='Select'
                    height='36px'
                    border='0px'
                    width={'96px'}
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    options={sensorIgnoreTime}
                    allowClear={false}
                  />
                </Form.Item>
                <span className='create-shipment-label-text'>after pickup</span>
              </>
            )}
          </Flex>
        </Col>
      )}
    </>
  );

  return (
    <>
      <div hidden={drawerView !== 'shipment'}>
        <Form
          form={form}
          name='CreateNewShipment'
          layout='vertical'
          onFinish={onFinish}
          autoComplete='off'
          onValuesChange={onFormValueCahnges}
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          initialValues={{
            ignore_sensor_measurement_at_start: 0,
          }}
        >
          <div className='drawer-title-header'>
            <CustomTitle
              text={'Track new shipment'}
              fontSize={20}
              fontWeight='700'
              lineHeight='32px'
              color='var(--text-primary)'
              marginBottom={0}
            />
          </div>

          <div className='create-shipment-body custom-scrollbar'>
            <Row gutter={[0, 16]}>
              {/* tracking ID */}
              <Col span={24}>
                <Form.Item
                  className='custom-input form-input-style'
                  label={
                    linksEnabled
                      ? 'Tracking ID'
                      : `${themeT('transportProvider', theme.themeType)} Tracking ID`
                  }
                  name='courier_tracking_id'
                  rules={[
                    {
                      required: false,
                      message: `Please enter ${themeT('transportProvider', theme.themeType).toLowerCase()} tracking ID`,
                    },
                    {
                      validator: (_, value) => {
                        if (value && value.trim() === '') {
                          return Promise.reject(
                            new Error('Only spaces are not allowed')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        if (getFieldValue('courier') && !value) {
                          return Promise.reject(
                            new Error(
                              `Please enter ${themeT('transportProvider', theme.themeType).toLowerCase()} tracking ID`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        if (
                          value &&
                          isSimulatorCourier(getFieldValue('courier')) &&
                          !isSimCourierId(value)
                        ) {
                          return Promise.reject(
                            new Error(
                              'Simulator tracking IDs look like SIM-YYMMDD-HHMMSS-XXXX'
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomInput
                    placeholder='Paste tracking number'
                    height='36px'
                    width='100%'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='38px'
                  />
                </Form.Item>
              </Col>
              {/* Transport provider */}
              <Col span={24}>
                <Form.Item
                  className='custom-input form-input-style'
                  label={`${themeT('transportProvider', theme.themeType)}`}
                  name='courier'
                  rules={[
                    {
                      required: false,
                      message: `Please select ${themeT('transportProvider', theme.themeType).toLowerCase()}`,
                    },

                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        if (getFieldValue('courier_tracking_id') && !value) {
                          return Promise.reject(
                            new Error(
                              `Please select ${themeT('transportProvider', theme.themeType).toLowerCase()}`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <CustomDynamicSelect
                    placeholder='Select'
                    height='36px'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    options={couriersList}
                    label='courier_label'
                    allowClear={true}
                    showSearch={couriersList.length > 10}
                    emptyContent={<EmptyTransportProvider image={emptyImg} />}
                  />
                </Form.Item>
              </Col>

              {(!linksEnabled || simulatorInUse) && (
                <Divider style={{ margin: '0px' }} />
              )}

              {/* Sensors */}

              {linksEnabled ? null : !showSensorFields ? (
                <>
                  <Col span={24}>
                    <AddItemButton
                      text={'Add sensor'}
                      fontSize='12px'
                      fontWeight='600'
                      marginTop='0px'
                      onClick={() => {
                        setShowSensorFields(true);
                        const rows = form.getFieldValue('sensor_rows') as
                          SensorFormRow[] | undefined;
                        if (!rows?.length) {
                          form.setFieldsValue({ sensor_rows: [{}] });
                        }
                      }}
                    />
                  </Col>
                </>
              ) : (
                <Form.List name='sensor_rows'>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => (
                        <Col span={24} key={field.key}>
                          <Flex
                            justify='space-between'
                            align='center'
                            style={{ marginBottom: '8px' }}
                          >
                            <CustomShipmentText
                              text={
                                index === 0
                                  ? 'Primary sensor'
                                  : `Sensor ${index + 1}`
                              }
                              fontWeight={600}
                            />
                            {fields.length >= 1 ? (
                              <span
                                role='button'
                                tabIndex={0}
                                onClick={() => {
                                  const isLastRow = fields.length <= 1;
                                  remove(field.name);
                                  if (isLastRow) {
                                    setShowSensorFields(false);
                                    form.setFieldsValue({
                                      sensor_rows: undefined,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLElement).click();
                                  }
                                }}
                                className='create-shipment-remove-sensor'
                                aria-label='Remove sensor row'
                              >
                                <DeleteIcon />
                              </span>
                            ) : null}
                          </Flex>
                          <Form.Item
                            className='custom-input form-input-style'
                            label='Sensor ID'
                            name={[field.name, 'sensor_tracking_id']}
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (
                                    value &&
                                    typeof value === 'string' &&
                                    value.trim() === ''
                                  ) {
                                    return Promise.reject(
                                      new Error('Only spaces are not allowed')
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                              ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                  const provider = getFieldValue([
                                    'sensor_rows',
                                    field.name,
                                    'sensor',
                                  ]);
                                  if (provider && !value) {
                                    return Promise.reject(
                                      new Error(
                                        'Please enter sensor tracking ID'
                                      )
                                    );
                                  }
                                  if (
                                    value &&
                                    isSimulatorSensor(provider) &&
                                    !isSimSensorId(value)
                                  ) {
                                    return Promise.reject(
                                      new Error(
                                        'Simulator sensor IDs look like SIM-YYMMDD-HHMMSS-XXXX-S1'
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              }),
                              ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                  if (
                                    !value ||
                                    typeof value !== 'string' ||
                                    !value.trim()
                                  ) {
                                    return Promise.resolve();
                                  }
                                  const currentTid = value
                                    .replace(/\s+/g, '')
                                    .toLowerCase();
                                  const rows = (getFieldValue('sensor_rows') ||
                                    []) as SensorFormRow[];
                                  const duplicateMatches = rows.filter(
                                    (r) =>
                                      r?.sensor_tracking_id &&
                                      typeof r.sensor_tracking_id ===
                                        'string' &&
                                      r.sensor_tracking_id
                                        .replace(/\s+/g, '')
                                        .toLowerCase() === currentTid
                                  );
                                  if (duplicateMatches.length > 1) {
                                    return Promise.reject(
                                      new Error(
                                        'Duplicate sensor tracking ID is not allowed'
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <CustomInput
                              placeholder='Paste tracking number'
                              height='36px'
                              width='100%'
                              border='0px'
                              bgColor='var(--light-gray)'
                              textColor='var(--text-primary)'
                              borderRadius='8px'
                              placeholderColor='var(--text-secondary)'
                              lineHeight='38px'
                            />
                          </Form.Item>
                          <Form.Item
                            className='custom-input form-input-style'
                            style={{ marginTop: '16px' }}
                            label='Sensor provider'
                            name={[field.name, 'sensor']}
                            rules={[
                              ({ getFieldValue }) => ({
                                validator: (_, value) => {
                                  const tid = getFieldValue([
                                    'sensor_rows',
                                    field.name,
                                    'sensor_tracking_id',
                                  ]);
                                  if (
                                    tid &&
                                    typeof tid === 'string' &&
                                    tid.trim() &&
                                    !value
                                  ) {
                                    return Promise.reject(
                                      new Error('Please select sensor')
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <CustomDynamicSelect
                              placeholder='Select'
                              width='100%'
                              height='36px'
                              border='0px'
                              bgColor='var(--light-gray)'
                              textColor='var(--text-primary)'
                              borderRadius='8px'
                              allowClear={true}
                              options={sensorsList}
                              label='sensor_label'
                              showSearch={sensorsList.length > 10}
                            />
                          </Form.Item>
                          {index < fields.length - 1 ? (
                            <Divider style={{ margin: '8px 0' }} />
                          ) : null}
                        </Col>
                      ))}
                      <Col span={24}>
                        <AddItemButton
                          text={'Add another sensor'}
                          fontSize='12px'
                          fontWeight='600'
                          marginTop='4px'
                          onClick={() => add({})}
                        />
                      </Col>
                    </>
                  )}
                </Form.List>
              )}

              {!linksEnabled && sensorOptions}

              {simulatorInUse ? (
                <Col span={24}>
                  <div className='simulation-scenario-card'>
                    <span className='simulation-scenario-card-title'>
                      Simulator scenario
                    </span>
                    {simulationScenario ? (
                      summarizeScenario(simulationScenario).map((line) => (
                        <span
                          key={line}
                          className='simulation-scenario-card-text'
                        >
                          {line}
                        </span>
                      ))
                    ) : (
                      <span className='simulation-scenario-card-text'>
                        A standard on-time journey starting now. Configure a
                        scenario to start in the past or to trigger an alert.
                      </span>
                    )}
                    <div className='simulation-scenario-card-actions'>
                      <CustomButton
                        text={
                          simulationScenario
                            ? 'Edit scenario'
                            : 'Configure scenario'
                        }
                        onClick={() => setDrawerView('scenario')}
                        bgColor='#000'
                        color='#fff'
                        height='36px'
                        borderRadius='8px'
                        border='0px'
                        fontSize='12px'
                        fontWeight={600}
                        padding='0 16px'
                      />
                      {simulationScenario ? (
                        <Button
                          type='text'
                          className='simulation-scenario-remove'
                          onClick={() => setSimulationScenario(null)}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Col>
              ) : null}

              <Divider style={{ margin: '0' }} />
              <Col span={24}>
                <Row gutter={[8, 16]}>
                  {/* Reference Number */}
                  {/* 14 span when link code added */}
                  <Col span={14}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label={`${themeT('referenceId', theme.themeType)}`}
                      name='reference_number'
                      rules={[
                        {
                          required: true,
                          message: `Please enter ${themeT('referenceId', theme.themeType)?.toLowerCase()}`,
                        },
                        {
                          validator: (_, value) => {
                            // Check if the value is only spaces or empty
                            if (value && value.trim() === '') {
                              return Promise.reject(
                                new Error('Only spaces are not allowed')
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <CustomInput
                        placeholder={`Your internal ${themeT('reference', theme.themeType)?.toLowerCase()} #`}
                        height='36px'
                        width='100%'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-primary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                      />
                    </Form.Item>
                  </Col>
                  {/* Link code  */}
                  <Col span={10}>
                    <Form.Item
                      className='custom-input form-input-style'
                      name='link_code'
                      label={
                        <Tooltip
                          title={
                            !isMobile ? (
                              <div className='create-shipment-link-code-hint'>
                                Will be appended to{' '}
                                {themeT('referenceId', theme.themeType)}.
                                Helpful in linking shipments
                              </div>
                            ) : null
                          }
                          overlayStyle={{ maxWidth: '154px' }}
                          placement='top'
                        >
                          <Typography.Text
                            className='dotted-ellipsis'
                            ellipsis={
                              isMobile
                                ? {
                                    tooltip: {
                                      title: `Will be appended to ${themeT(
                                        'referenceId',
                                        theme.themeType
                                      )}. Helpful in linking shipments. ${themeT(
                                        'linkCode',
                                        theme.themeType
                                      )} (optional)`,
                                      overlayClassName: 'link-code-tooltip',
                                    },
                                  }
                                : false
                            }
                          >
                            {`${themeT('linkCode', theme.themeType)} (optional)`}
                          </Typography.Text>
                        </Tooltip>
                      }
                      rules={[
                        {
                          required: false,
                          pattern: /^[a-zA-Z0-9]{0,255}$/,
                          message:
                            'Please enter up to 255 alphanumeric characters',
                        },
                      ]}
                    >
                      <CustomInput
                        placeholder='Code'
                        height='36px'
                        width='100%'
                        border='0px'
                        bgColor='var(--light-gray)'
                        textColor='var(--text-primary)'
                        borderRadius='8px'
                        placeholderColor='var(--text-secondary)'
                        lineHeight='38px'
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              {/* Teams  */}

              {/* teams  */}
              {hasValidTeams && (
                <>
                  <Col span={24}>
                    <Form.Item
                      className='custom-input form-input-style'
                      label={`${themeT('team', theme.themeType)}s`}
                      name='teams'
                      rules={[
                        {
                          required: true,
                          message: `Please select ${themeT('team', theme.themeType)?.toLowerCase()}s`,
                        },
                      ]}
                    >
                      <TeamDropdownSelect
                        teamsOptions={allTeams}
                        selectedTeams={selectedTeams}
                        onChange={setSelectedTeams}
                        formState='add'
                        onSearch={handleTeamsSearch}
                        teamsSearch={teamsSearch}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}

              {/* Products */}
              <Col span={24}>
                {linksEnabled ? (
                  <Form.Item
                    className='custom-input form-input-style'
                    label='Products'
                  >
                    {renderProductPicker(
                      selectedProducts.map((p) => p.id),
                      changeShipmentProducts,
                      'Products'
                    )}
                  </Form.Item>
                ) : (
                  <ProductMultiSelectField
                    token={token}
                    form={form}
                    selectedProducts={selectedProducts}
                    onProductsChange={setSelectedProducts}
                    getProductName={getProductName}
                    mergeIntoLookup={mergeIntoLookup}
                    paginatedProducts={paginatedProducts}
                    productCatalogTotalCount={productCatalogTotalCount}
                    productDropdownLoading={productDropdownLoading}
                    productSearchInputKey={productSearchInputKey}
                    productSearch={productSearch}
                    setProductSearch={setProductSearch}
                    handlePopupScroll={handleProductPopupScroll}
                    onOpenAddProduct={() => setDrawerView('product_form')}
                  />
                )}
              </Col>
              {/* product quantity */}
              {selectedProducts.length > 0 && (
                <Col span={24}>
                  <ProductQuantityRows
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    getProductName={getProductName}
                    updateQuantity={updateQuantity}
                    removeProduct={
                      linksEnabled
                        ? (id) =>
                            changeShipmentProducts(
                              selectedProducts
                                .map((p) => p.id)
                                .filter((productId) => productId !== id)
                            )
                        : removeProduct
                    }
                  />
                </Col>
              )}

              {/* from location    */}
              <Row style={{ gap: 12, width: '100%' }}>
                <Col span={24}>
                  <GoogleMapsProvider>
                    <div className='create-shipment-location-row'>
                      <LocationAutoCompleteShipment
                        locationList={locationList}
                        handleAddressPlaceSelect={(
                          place,
                          _field,
                          selectOpts
                        ) => {
                          handleAddressPlaceSelect(
                            place as LocationOption & { address?: string },
                            'from_location',
                            selectOpts
                          );
                        }}
                        fieldName='from_location'
                        label='Pickup & delivery'
                        width={'90%'}
                        className='custom-input form-input-style'
                        placeholder='Enter pick-up address/location'
                        externalValue={fromDisplayValue}
                        onInputChange={(val) =>
                          handleAddressInputChange('from', val)
                        }
                        onDetailHydrationChange={handleDetailHydrationChange}
                        isSaved={
                          !!(fromLocation?.id && !fromLocation?.placeName) &&
                          !fromEditMode
                        }
                        onSavedClick={() => setFromEditMode(true)}
                        suffixIcon={
                          canCreateLocation && fromDisplayValue ? (
                            fromLocation?.id && !fromLocation?.placeName ? (
                              <svg
                                width='11'
                                height='14'
                                viewBox='0 0 14 18'
                                fill='var(--text-primary)'
                                className='create-shipment-location-icon'
                                onClick={() => handleDetachLocation('from')}
                              >
                                <path d='M1 1h12v16l-6-4-6 4V1z' />
                              </svg>
                            ) : (
                              <svg
                                width='11'
                                height='14'
                                viewBox='0 0 14 18'
                                fill='none'
                                stroke='#888'
                                strokeWidth='1.5'
                                className='create-shipment-location-icon'
                                onClick={() => handleBookmarkClick('from')}
                              >
                                <path
                                  d='M1 1h12v16l-6-4-6 4V1z'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            )
                          ) : undefined
                        }
                      />
                      <span className='create-shipment-from-polygon-button'>
                        <CustomButton
                          isFormValid={!fromDisplayValue || fromDetailHydrating}
                          image={
                            polygonFrom.length >= 3 ? undefined : EditLocation
                          }
                          icon={
                            polygonFrom.length >= 3 ? (
                              <GeoFenceIcon color='#fff' />
                            ) : undefined
                          }
                          iconPosition={'left'}
                          border='none'
                          width='36px'
                          height='36px'
                          borderRadius='50%'
                          bgColor={polygonFrom.length >= 3 ? '#000' : '#f5f5f5'}
                          color='var(--text-primary)'
                          boxShadow='none'
                          onClick={() => openMapModal('from_location')}
                          padding={'0'}
                        />
                      </span>
                    </div>
                  </GoogleMapsProvider>
                </Col>
                {/* to location  */}
                <Col span={24}>
                  <GoogleMapsProvider>
                    <div className='create-shipment-location-row'>
                      <LocationAutoCompleteShipment
                        locationList={locationList}
                        handleAddressPlaceSelect={(place, _field, selectOpts) =>
                          handleAddressPlaceSelect(
                            place as LocationOption & { address?: string },
                            'to_location',
                            selectOpts
                          )
                        }
                        fieldName='to_location'
                        label=''
                        width={'90%'}
                        className='label-13px'
                        placeholder='Enter delivery address/location'
                        externalValue={toDisplayValue}
                        onInputChange={(val) =>
                          handleAddressInputChange('to', val)
                        }
                        onDetailHydrationChange={handleDetailHydrationChange}
                        isSaved={
                          !!(toLocation?.id && !toLocation?.placeName) &&
                          !toEditMode
                        }
                        onSavedClick={() => setToEditMode(true)}
                        suffixIcon={
                          canCreateLocation && toDisplayValue ? (
                            toLocation?.id && !toLocation?.placeName ? (
                              <svg
                                width='11'
                                height='14'
                                viewBox='0 0 14 18'
                                fill='var(--text-primary)'
                                className='create-shipment-location-icon'
                                onClick={() => handleDetachLocation('to')}
                              >
                                <path d='M1 1h12v16l-6-4-6 4V1z' />
                              </svg>
                            ) : (
                              <svg
                                width='11'
                                height='14'
                                viewBox='0 0 14 18'
                                fill='none'
                                stroke='#888'
                                strokeWidth='1.5'
                                className='create-shipment-location-icon'
                                onClick={() => handleBookmarkClick('to')}
                              >
                                <path
                                  d='M1 1h12v16l-6-4-6 4V1z'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            )
                          ) : undefined
                        }
                      />
                      <span className='create-shipment-to-polygon-button'>
                        <CustomButton
                          isFormValid={!toDisplayValue || toDetailHydrating}
                          image={
                            polygonTo.length >= 3 ? undefined : EditLocation
                          }
                          icon={
                            polygonTo.length >= 3 ? (
                              <GeoFenceIcon color='#fff' />
                            ) : undefined
                          }
                          iconPosition={'left'}
                          border='none'
                          width='36px'
                          height='36px'
                          borderRadius='50%'
                          bgColor={polygonTo.length >= 3 ? '#000' : '#f5f5f5'}
                          color='var(--text-primary)'
                          boxShadow='none'
                          onClick={() => openMapModal('to_location')}
                          padding={'0'}
                        />
                      </span>
                    </div>
                  </GoogleMapsProvider>
                </Col>
              </Row>
              {/* map  */}
              {selectedLatLng1?.lat != null &&
                selectedLatLng1?.lng != null &&
                selectedLatLng2?.lat != null &&
                selectedLatLng2?.lng != null && (
                  <Col span={24}>
                    <div className='map-container is-create-shipment-review-map'>
                      <GoogleMapsProvider>
                        <ShipmentPreviewMap
                          currentView='summary'
                          // The create preview has no tracking points to click.
                          onPointClicked={() => undefined}
                          fromLocation={fromLocation ?? undefined}
                          fromLatLng={
                            toPolygonPoint(selectedLatLng1) ?? undefined
                          }
                          toLocation={toLocation ?? undefined}
                          toLatLng={
                            toPolygonPoint(selectedLatLng2) ?? undefined
                          }
                          // lastCoordinates={lastPoint}
                          // shipment={shipment}
                          height={'240px'}
                          isLargeScreen={false}
                        />
                      </GoogleMapsProvider>
                    </div>
                  </Col>
                )}

              {linksEnabled && (
                <>
                  <Divider style={{ margin: '0' }} />
                  <Col span={24}>
                    <SensorSection
                      rows={sensorRows}
                      onRowsChange={changeSensorRows}
                      sensorsList={sensorsList}
                      getProductName={getProductName}
                      renderProductPicker={(value, onChange) =>
                        renderProductPicker(value, onChange, 'Sensor products')
                      }
                      onEditingChange={setSensorCardOpen}
                      isSimulatorSensor={isSimulatorSensor}
                      suggestSensorId={suggestSensorId}
                    >
                      {sensorOptions}
                    </SensorSection>
                  </Col>
                </>
              )}

              {/* submit buttons  */}
              <div className='drawer-custom-footer'>
                <CustomButton
                  text={'Save'}
                  width={'100%'}
                  height='48px'
                  borderRadius='8px'
                  bgColor={isFormValid ? '#F3F3F3' : '#000'}
                  color={isFormValid ? '#A6A6A6' : '#fff'}
                  border='0px'
                  fontSize='16px'
                  fontWeight={600}
                  // htmlType="submit"
                  onClick={() => {
                    form.submit();
                  }}
                  isFormValid={isFormValid}
                />
                <CustomButton
                  key='button1'
                  width={'100%'}
                  marginTop='8px'
                  onClick={() => handleCloseDrawer()}
                  text={'Cancel'}
                  bgColor='transparent'
                  color='black'
                  fontSize='16px'
                  fontWeight='600'
                  boxShadow='none'
                  height='48px'
                />
              </div>
            </Row>
          </div>
        </Form>

        {/* Save location modal */}
        <SaveLocationModal
          open={saveLocationModalOpen}
          address={
            saveLocationTarget === 'from'
              ? fromDisplayValue || ''
              : toDisplayValue || ''
          }
          onSave={handleSaveLocation}
          onCancel={() => setSaveLocationModalOpen(false)}
        />

        {/* Drawer polygon modal  */}
        <CustomModal
          modalOpen={modalOpen}
          setModalOpen={handleSetModalOpen}
          modalTitle={`${modalName}`}
          modalText=''
          modalSize={680}
          buttonText1={'Cancel'}
          buttonText2={'Submit'}
          isFormValid={isFormValid}
          onConfirmClick={onModalConfirm}
        >
          <GoogleMapsProvider>
            <div className='polygon-map-shell'>
              <PolygonFenceMap
                center={polygonMapCenter}
                polygon={polygonSession.polygon}
                closed={polygonSession.isClosed}
                resetTrigger={polygonSession.resetTrigger}
                onPolygonChange={polygonSession.setPolygon}
                onClosedChange={polygonSession.setIsClosed}
              />
              {polygonSession.polygon.length > 0 && (
                <button
                  type='button'
                  className='polygon-map-undo-btn'
                  onClick={polygonSession.undo}
                >
                  <UndoIcon size={12} />
                  <span>Undo</span>
                </button>
              )}
            </div>
          </GoogleMapsProvider>
        </CustomModal>
      </div>

      {drawerView === 'scenario' ? (
        <SimulationScenarioView
          value={simulationScenario}
          loadProducts={loadScenarioProducts}
          thresholds={alertThresholds}
          onSave={(config) => {
            applyScenarioChoices(config, simulationScenario?.product?.id);
            setSimulationScenario(config);
            setDrawerView('shipment');
          }}
          onCancel={() => setDrawerView('shipment')}
        />
      ) : null}

      <div hidden={drawerView !== 'product_form'}>
        <SettingsProductDrawerForm
          form={productDrawerForm}
          id={null}
          showBackButton
          handleCloseDrawer={() => {
            finishAddProduct();
            setDrawerView('shipment');
            productDrawerForm.resetFields();
          }}
          handleFetchAllProduct={(created?: Product) => {
            setProductsRefreshKey((prev) => prev + 1);
            finishAddProduct(created);
            setDrawerView('shipment');
            productDrawerForm.resetFields();
          }}
        />
      </div>
    </>
  );
};

export default CreateShipmentDrawer;
